import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, MapPin, Phone, Mail, Clock, Heart, ChevronDown, Volume2, VolumeX } from 'lucide-react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import './theme.css';

const META: TemplateMetadata = {
  id: 'royal-ganesh-interactive',
  name: 'Royal Ganesh — Interactive',
  occasionType: 'WEDDING',
  description: 'A premium, animated, interactive wedding invitation featuring Ganesha, custom floral motifs, and a countdown.',
  isPremium: true,
  tags: ['wedding', 'ganesh', 'interactive', 'animated', 'premium', 'royal', 'blue'],
  colorScheme: { primary: '#BE123C', accent: '#D4AF37', bg: '#0b060f' },
};

// Internal self-contained Countdown component
function Countdown({ targetDate }: { targetDate: string }) {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const calculateTime = () => {
      const difference = +new Date(targetDate) - +new Date();
      if (difference <= 0) return;

      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      });
    };

    calculateTime();
    const interval = setInterval(calculateTime, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  const timeBlocks = [
    { label: 'Days', value: timeLeft.days },
    { label: 'Hrs', value: timeLeft.hours },
    { label: 'Min', value: timeLeft.minutes },
    { label: 'Sec', value: timeLeft.seconds },
  ];

  return (
    <div className="flex justify-center gap-4 my-6 font-display-ganesh">
      {timeBlocks.map((block) => (
        <div
          key={block.label}
          className="flex h-16 w-16 flex-col items-center justify-center rounded-xl border border-yellow-500/20 bg-black/40 backdrop-blur-sm"
        >
          <span className="text-xl font-bold text-yellow-500">{block.value}</span>
          <span className="text-[9px] uppercase tracking-wider text-gray-400">{block.label}</span>
        </div>
      ))}
    </div>
  );
}

export function RoyalGaneshInteractive({ data }: { data: InvitationData }) {
  const [activeSection, setActiveSection] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Extract variables safely with fallbacks
  const groom = data.customData?.wedding?.groom;
  const bride = data.customData?.wedding?.bride;

  const groomName = groom?.fullName || groom?.nickname || 'Vishal';
  const groomFather = groom?.fatherName || 'Mr. Kuldeep';
  const groomMother = groom?.motherName || 'Mrs. Sunita';

  const brideName = bride?.fullName || bride?.nickname || 'Dyaneshwari';
  const brideFather = bride?.fatherName || 'Mr. Dinesh Chandra Saxena';
  const brideMother = bride?.motherName || 'Late Mrs. Meena Saxena';

  const eventName = data.eventName || 'Wedding';
  
  // Format Date gracefully
  let eventDateString = 'Friday, 3rd Oct 2025';
  let rawDate = '2025-10-03';
  if (data.eventDate) {
    try {
      rawDate = data.eventDate.split('T')[0];
      const d = new Date(data.eventDate);
      eventDateString = d.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (e) {}
  }

  const weddingEvent = data.customData?.wedding?.weddingEvents?.[0];
  const eventTime = weddingEvent?.startTime || '7:00 pm onwards';
  const venueCity = data.eventVenue || 'Solapur';
  const venueAddress = weddingEvent?.venue || data.eventVenue || 'Solapur Premium Hotel & Resort';
  
  // Fallbacks for missing text
  const hostName = data.hostName || 'Bhatnagar Family';
  const customMessage = data.customMessage || 'A beautiful journey of two souls beginning with a promise, celebrated under the blessings of family and friends.';

  // Background videos: two large videos (50 MiB + 41 MiB) are served from
  // Cloudflare R2 (media.invitecloud.app) to stay under Cloudflare Pages'
  // 25 MiB per-file limit. Ganesha Golden Clipart.mp4 (16 MiB) stays local.
  const MEDIA_BASE = import.meta.env.VITE_MEDIA_BASE_URL || 'https://media.invitecloud.app';

  const bgVideos = [
    '/assets/royal-ganesh-interactive/Ganesha Golden Clipart.mp4', // 16 MiB — under limit, stays local
    `${MEDIA_BASE}/templates/royal-ganesh-interactive/www.psdking.com.mp4`,
    `${MEDIA_BASE}/templates/royal-ganesh-interactive/www.psdking.com.mp4`,
    `${MEDIA_BASE}/templates/royal-ganesh-interactive/www.psdking.com.mp4`,
    `${MEDIA_BASE}/templates/royal-ganesh-interactive/www.psdking.com.mp4`,
    `${MEDIA_BASE}/templates/royal-ganesh-interactive/www.psdking.com-3.mp4`,
  ];

  // Scroll handler to swap background loops
  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = window.scrollY;
      const height = window.innerHeight;
      const index = Math.min(
        bgVideos.length - 1,
        Math.floor((scrollPos + height / 2) / height)
      );
      setActiveSection(index);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Audio lifecycle
  useEffect(() => {
    audioRef.current = new Audio('/assets/royal-ganesh-interactive/music.mp3');
    audioRef.current.loop = true;
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  const toggleMusic = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(() => {});
    }
    setIsPlaying(!isPlaying);
  };

  const handleAddToCalendar = () => {
    const calendarTitle = `${groomName} & ${brideName}'s ${eventName}`;
    const dateFormatted = rawDate.replace(/\-/g, '');
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
      calendarTitle
    )}&dates=${dateFormatted}T190000Z/${dateFormatted}T220000Z&details=${encodeURIComponent(
      customMessage
    )}&location=${encodeURIComponent(venueAddress)}`;
    window.open(googleCalendarUrl, '_blank');
  };

  return (
    <div className="relative min-h-screen text-gray-200" style={{ backgroundColor: '#0b060f' }}>
      
      {/* 1. Video Background Engine */}
      <div className="fixed inset-0 -z-10 h-full w-full overflow-hidden bg-black">
        <div className="absolute inset-0 bg-black/60 z-10" />
        <video
          key={bgVideos[activeSection]}
          autoPlay
          muted
          loop
          playsInline
          className="h-full w-full object-cover transition-opacity duration-1000"
          style={{ opacity: 0.7 }}
        >
          <source src={bgVideos[activeSection]} type="video/mp4" />
        </video>
      </div>

      {/* Floating Audio Controller */}
      <button
        onClick={toggleMusic}
        className="fixed bottom-6 right-6 z-50 flex h-12 w-12 items-center justify-center rounded-full border border-yellow-500/30 bg-black/60 text-yellow-500 backdrop-blur-md transition-all hover:scale-110 active:scale-95"
      >
        {isPlaying ? <Volume2 className="h-5 w-5 animate-pulse" /> : <VolumeX className="h-5 w-5" />}
      </button>

      {/* 2. Scrollable Presentation Layers */}
      <main className="relative z-20 mx-auto max-w-lg px-4 md:max-w-2xl">
        
        {/* SCENE 1: SHREE GANESH */}
        <section className="flex h-screen flex-col items-center justify-center text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.5 }}
            className="flex flex-col items-center gap-4"
          >
            <img
              src="/assets/royal-ganesh-interactive/extracted/parents_detail/ganpati.png"
              alt="Ganpati"
              className="h-28 w-auto animate-float-slow-ganesh"
            />
            <h2 className="font-italic-ganesh text-2xl tracking-widest text-yellow-500/80">
              || श्री गणेशाय नमः ||
            </h2>
            <div className="h-[2px] w-24 bg-gradient-to-r from-transparent via-yellow-500 to-transparent my-2" />
            <motion.h1 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 1 }}
              className="font-display-ganesh text-4xl font-bold tracking-wider text-yellow-500 md:text-5xl"
            >
              Shree Ganesh
            </motion.h1>
            <p className="mt-4 max-w-xs font-italic-ganesh text-sm text-gray-400">
              May Lord Ganesha bless this auspicious beginning with peace, prosperity, and joy.
            </p>
          </motion.div>
          <div className="absolute bottom-10 animate-bounce">
            <ChevronDown className="h-6 w-6 text-yellow-500/60" />
          </div>
        </section>

        {/* SCENE 2: WELCOME LOGO */}
        <section className="flex h-screen flex-col items-center justify-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="flex flex-col items-center gap-6 rounded-2xl border border-yellow-500/10 bg-black/40 p-8 backdrop-blur-md w-full"
          >
            <img
              src="/assets/royal-ganesh-interactive/extracted/logo/logo.png"
              alt="Celebration Logo"
              className="h-32 w-auto animate-float-fast-ganesh"
            />
            <h2 className="font-display-ganesh text-xs uppercase tracking-widest text-yellow-500/60">
              Save The Date
            </h2>
            <div className="font-display-ganesh text-3xl font-semibold tracking-wide text-yellow-500">
              {groomName} & {brideName}
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-gray-300">
              {customMessage}
            </p>
          </motion.div>
        </section>

        {/* SCENE 3: BRIDE & GROOM DETAILS */}
        <section className="flex h-screen flex-col items-center justify-center text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 1.2 }}
            className="w-full rounded-2xl border border-yellow-500/10 bg-black/50 p-8 backdrop-blur-md"
          >
            <span className="font-italic-ganesh text-lg text-gray-400">of our beloved son</span>
            
            <motion.h2 
              initial={{ scale: 0.9 }}
              whileInView={{ scale: 1 }}
              className="font-calligraphy-ganesh my-4 text-6xl font-normal text-yellow-500 drop-shadow-md md:text-7xl"
            >
              {groomName}
            </motion.h2>
            
            <span className="font-italic-ganesh text-sm text-yellow-500/60 block mb-2">
              S/o {groomFather} and {groomMother}
            </span>

            <div className="flex items-center justify-center gap-3 my-4">
              <div className="h-[1px] w-12 bg-yellow-500/30" />
              <Heart className="h-4 w-4 text-red-500 animate-pulse fill-red-500" />
              <div className="h-[1px] w-12 bg-yellow-500/30" />
            </div>

            <span className="font-italic-ganesh text-lg text-gray-400">with</span>

            <motion.h2 
              initial={{ scale: 0.9 }}
              whileInView={{ scale: 1 }}
              className="font-calligraphy-ganesh my-4 text-6xl font-normal text-yellow-500 drop-shadow-md md:text-7xl"
            >
              {brideName}
            </motion.h2>

            <span className="font-italic-ganesh text-sm text-yellow-500/60 block">
              D/o {brideFather} and {brideMother}
            </span>
          </motion.div>
        </section>

        {/* SCENE 4: PROGRAMME & SCHEDULE */}
        <section className="flex h-screen flex-col items-center justify-center text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="w-full rounded-2xl border border-yellow-500/10 bg-black/50 p-8 backdrop-blur-md"
          >
            <h2 className="font-display-ganesh text-3xl font-bold tracking-wider text-yellow-500">
              {eventName}
            </h2>
            
            <div className="my-6 flex justify-center gap-4 text-gray-300 font-display-ganesh">
              <div className="flex flex-col items-center p-3 border-r border-yellow-500/20">
                <Calendar className="h-5 w-5 text-yellow-500 mb-1" />
                <span className="text-sm font-semibold">{eventDateString.split(',')[0]}</span>
              </div>
              <div className="flex flex-col items-center p-3 border-r border-yellow-500/20">
                <Clock className="h-5 w-5 text-yellow-500 mb-1" />
                <span className="text-sm font-semibold">{eventTime}</span>
              </div>
              <div className="flex flex-col items-center p-3">
                <MapPin className="h-5 w-5 text-yellow-500 mb-1" />
                <span className="text-sm font-semibold">{venueCity}</span>
              </div>
            </div>

            <button
              onClick={handleAddToCalendar}
              className="mt-4 flex items-center justify-center gap-2 mx-auto rounded-full bg-gradient-to-r from-yellow-600 to-yellow-500 px-6 py-2.5 font-display-ganesh text-sm font-semibold text-black shadow-lg transition-all duration-300 hover:scale-105 hover:from-yellow-500 hover:to-yellow-400"
            >
              <Calendar className="h-4 w-4" />
              Add to Calendar
            </button>
          </motion.div>
        </section>

        {/* SCENE 5: VENUE DETAILS & MAP */}
        <section className="flex h-screen flex-col items-center justify-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="w-full rounded-2xl border border-yellow-500/10 bg-black/50 p-8 backdrop-blur-md"
          >
            <MapPin className="h-10 w-10 text-yellow-500 mx-auto mb-4" />
            <h3 className="font-display-ganesh text-xl font-bold tracking-wide text-yellow-500">
              The Venue
            </h3>
            <p className="mt-4 font-italic-ganesh text-lg text-white">
              {venueAddress.split(',')[0]}
            </p>
            <p className="mt-2 text-sm text-gray-400 leading-relaxed">
              {venueAddress}
            </p>

            <div className="mt-6 flex flex-col gap-3">
              <button
                onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venueAddress)}`, '_blank')}
                className="w-full rounded-full border border-yellow-500/30 bg-yellow-500/10 py-2.5 font-display-ganesh text-sm font-semibold text-yellow-500 transition-all hover:bg-yellow-500/20"
              >
                Navigate with Maps
              </button>
            </div>
          </motion.div>
        </section>

        {/* SCENE 6: COUNTDOWN & FOOTER */}
        <section className="flex h-screen flex-col items-center justify-center text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2 }}
            className="w-full rounded-2xl border border-yellow-500/10 bg-black/50 p-8 backdrop-blur-md"
          >
            <h3 className="font-italic-ganesh text-lg text-gray-400">Awaiting Your Presence</h3>
            <h2 className="font-display-ganesh text-3xl font-bold tracking-wide text-yellow-500 my-4">
              {hostName}
            </h2>

            <div className="h-[1px] w-36 bg-gradient-to-r from-transparent via-yellow-500/30 to-transparent mx-auto my-6" />

            <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">
              Wedding Countdown
            </p>
            <Countdown targetDate={`${rawDate}T19:00:00`} />

            {data.rsvpLink && (
              <div className="mt-6 flex justify-center">
                <a
                  href={data.rsvpLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 rounded-full border border-yellow-500/30 bg-yellow-500/10 px-6 py-2.5 font-display-ganesh text-sm font-semibold text-yellow-500 hover:bg-yellow-500/20 transition-all hover:scale-105 active:scale-95"
                >
                  <Mail className="h-4 w-4" />
                  RSVP Now
                </a>
              </div>
            )}
          </motion.div>
          
          <p className="absolute bottom-6 text-[10px] tracking-widest text-gray-600 uppercase">
            Powered by InviteCloud
          </p>
        </section>

      </main>
    </div>
  );
}

registry.register({ metadata: META, component: RoyalGaneshInteractive });
